/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { IGeneric } from '../../interfaces';
import {
    BODY_START_LOAD_POINT,
    DATA_LOAD_POINT,
    HEAD_ELEMENT_IDENTIFIER_ATTR,
    HEAD_START_LOAD_POINT,
    HtmlHeadElement,
    IHtmlHead
} from './IHtmlHeadModel';
const DOMAttributeNames: IGeneric<unknown> = {
    acceptCharset: 'accept-charset',
    className: 'class',
    htmlFor: 'for',
    httpEquiv: 'http-equiv'
};

/**
 * update title of the page
 * @function updateTitle
 * @param {HtmlHeadElement} title - final derived title component
 */
const updateTitle = (titleComponent: HtmlHeadElement): void => {
    let title: string = '';
    if (titleComponent) {
        const { children } = titleComponent.props;
        title = typeof children === 'string' ? children : '';
    }

    const initialData = window && (<any>window).___initialData___;
    switch (titleComponent && titleComponent.props['data-ms-action']) {
        case 'format':
            if (initialData && initialData.pageRoot) {
                const titleTemplate = initialData.pageRoot.titleTemplate;
                title = titleTemplate.replace(/\{0\}/, title);
            }

            break;
        case 'append':
            title = document.title !== title ? `${document.title} ${title}` : title;
            break;
        case 'override':
        default:
    }

    if (document.title !== title) {
        document.title = title;
    }
};

/**
 * update head tag on change, on client side
 * @function reactElementToDOM
 * @param {HtmlHeadElement} element - final react element that needs to be added to DOM
 * @return {HTMLElement} - html dom element
 */
const reactElementToDOM = (element: HtmlHeadElement): HTMLElement => {
    const tagType = typeof element.type === 'string' ? element.type : '';
    const el = document.createElement(tagType);
    Object.keys(element.props).forEach((property: string) => {
        if (property !== 'children' && property !== 'dangerouslySetInnerHTML') {
            const attr = DOMAttributeNames[property] || property.toLowerCase();
            el.setAttribute(attr, element.props[property]);
        }
    });

    const { children, dangerouslySetInnerHTML } = element.props;
    if (el && dangerouslySetInnerHTML) {
        el.innerHTML = dangerouslySetInnerHTML.__html || '';
    } else if (el && children) {
        el.textContent = typeof children === 'string' ? children : children.join('');
    }

    return el;
};

/**
 * Filters a script based on the filterFromBody parameter
 * If filterFromBody is true, only those scripts that have head load points will
 * return true. Otherwise, only scripts with body load points will return true.
 *
 * @param filterBody Set to true to filter out scripts in body. False to filter out scripts in the head
 * @param t The script node
 */
const filterScripts = (filterBody: boolean, t: Node): boolean => {
    const el = <Element>t;
    if (el.tagName === 'SCRIPT') {
        const attrVal = el.getAttribute(DATA_LOAD_POINT);
        if (attrVal === 'bodyStart' || attrVal === 'bodyEnd' || attrVal === null) {
            return filterBody ? false : true;
        }
    }
    return filterBody ? true : false;
};

/**
 * Adds an HTML node to a particular slot
 *
 * @param slot The parent Element to which the HTML node should be added
 * @param loadPointToLookFor The load point type to look for to determine if the node should be prepended to append to the parent node
 * @param t The HTML node to add
 */
const addElementToHtmlSlot = (slot: HTMLElement, loadPointToLookFor: string, t: Node) => {
    const loadPoint = (<Element>t).getAttribute(DATA_LOAD_POINT);
    if (loadPoint && loadPoint === loadPointToLookFor) {
        slot.prepend(t);
    } else {
        slot.appendChild(t);
    }
};

/**
 * update head tag on change, on client side
 * @function doUpdateHead
 * @param {HtmlHead} head - final list of merged tags
 * @return {HtmlHead} - final list of merged tags
 */
const updateElements = (tagType: string, components: HtmlHeadElement[]): void => {
    const headEl = document.getElementsByTagName('head')[0];
    const bodyEl = document.getElementsByTagName('body')[0];
    let oldTags: HTMLElement[] = Array.prototype.slice.call(headEl.querySelectorAll(`${tagType}.${HEAD_ELEMENT_IDENTIFIER_ATTR}`));
    // sepcial condition for scripts: read script tags from body tag as well
    if (tagType === 'script') {
        const bodyScriptTags = Array.prototype.slice.call(bodyEl.querySelectorAll(`script.${HEAD_ELEMENT_IDENTIFIER_ATTR}`));
        oldTags = oldTags.concat(bodyScriptTags);
    }
    const newTags = components.map(reactElementToDOM).filter((newTag: HTMLElement) => {
        const len = oldTags.length;
        for (let i = 0; i < len; i++) {
            const oldTag = oldTags[i];
            oldTag.removeAttribute('data-reactroot');
            if (oldTag.isEqualNode(newTag)) {
                oldTags.splice(i, 1);
                return false;
            } else if (
                oldTag.nodeName === 'SCRIPT' &&
                (oldTag.innerHTML === newTag.innerHTML || oldTag.getAttribute('src') === newTag.getAttribute('src'))
            ) {
                oldTags.splice(i, 1);
                return false;
            }
        }
        return true;
    });
    // Remove all the old tags and add new tags in the correct places
    oldTags.forEach((t: Node) => t.parentNode && t.parentNode.removeChild(t));
    // First, filter out all the script tags that are in the body and then reinsert
    // the new tags in the head in their appropriate locations based on the load-point (if applicable)
    newTags
        .filter((t: Node) => {
            return filterScripts(true, t);
        })
        .forEach((t: Node) => {
            addElementToHtmlSlot(headEl, HEAD_START_LOAD_POINT, t);
        });
    // If any script tags were defined in the body, add these script tags back into the body
    // based on their load-points
    newTags
        .filter((t: Node) => {
            return filterScripts(false, t);
        })
        .forEach((t: Node) => {
            addElementToHtmlSlot(bodyEl, BODY_START_LOAD_POINT, t);
        });
};

/**
 * update head tag on change, on client side
 * @function doUpdateHead
 * @param {HtmlHead} head - final list of merged tags
 */
const doUpdateHead = (head: IHtmlHead): void => {
    const tags: IGeneric<unknown> = {};
    head.tags.forEach((h: HtmlHeadElement) => {
        const headType = h.type.toString();
        const components = tags[headType] || [];
        components.push(h);
        tags[headType] = components;
    });

    const titleTag = tags.title;
    updateTitle(titleTag && titleTag.length > 0 ? titleTag[0] : null);

    const types = ['meta', 'link', 'style', 'script'];
    types.forEach((tagType: string) => {
        updateElements(tagType, tags[tagType] || []);
    });
};

/**
 * update head on change, on client side
 * @function updateHead
 * @param {HtmlHead} head - final list of merged tags
 */
export const updateHead = (head: IHtmlHead): void => {
    doUpdateHead(head);
};
